import React, { useCallback, useMemo, useReducer } from 'react'
import propTypes from 'prop-types'

import LoadingIndicator from '@/components/LoadingIndicator'
import { ReportTable } from '@/components/report-tables'
import ErrorBoundary from '@/components/ErrorBoundary'
import { Row, Col, FormGroup, Label, ButtonGroup } from 'reactstrap'
import Resx from '@/components/Resx'
import { Button } from '@/components/button'
import context from '@/context'
import { DropDownList } from '@/components/dropDownList'
import { actionCreators, actionTypes } from '@/store/OutputReports'
import { EditableTableContext } from '@/components/report-tables/editable/editableTableContext'
import { useDispatch } from '@/hooks/dispatch'
import { TableTools } from '@/components/reporting/tableTools'
import { getCellMetadataFromReportSubmissionData } from '@/store/utils/reportUtils'
import { getStore } from '@/store'
import { openConfirmDialog, openErrorDialog } from '@/utils/dialogHelpers'

const initialState = {
  bankTypes: [],
  institutionGroups: []
}

const actions = {
  selectBankTypes: 'selectBankTypes',
  selectInstitutionGroups: 'selectInstitutionGroups'
}

function reducer (state = initialState, action) {
  switch (action.type) {
    case actions.selectBankTypes: return { ...state, bankTypes: action.data }
    case actions.selectInstitutionGroups: return { ...state, institutionGroups: action.data }
  }
}

function OutputReportTable ({ isRtl,
  sourceHeaders,
  sourceRowHeaders,
  sourceData,
  cellMetadata,
  isLoading,
  outputReport,
  rowFilter }) {
  const [state, dispatch] = useReducer(reducer, { ...initialState, ...rowFilter })
  const [getFilteredIds] = useDispatch([actionCreators.getFiltered])

  const { id, reportSubmissionGroupId } = outputReport || {}

  const selectBankTypes = useCallback(({ value }) => dispatch({ type: actions.selectBankTypes, data: value }), [])
  const selectInstitutionGroups = useCallback(({ value }) => dispatch({ type: actions.selectInstitutionGroups, data: value }), [])
  const onReportLock = useCallback(() => {
    openConfirmDialog({
      customConfirmText: 'Reporting.LockReportPrompt',
      onAction: () => {
        context.api.outputReportGroupService.lockOutputReport(id)
          .then(response => {
            const cellMetadata = getCellMetadataFromReportSubmissionData(response)

            getStore().dispatch({
              type: actionTypes.ReportLocked,
              data: {
                data: response,
                cellMetadata: cellMetadata
              }
            })
          })
          .catch(() => openErrorDialog({}))
      }
    })
  }, [id])

  const getFiltered = useCallback(() => getFilteredIds(id, reportSubmissionGroupId, state.bankTypes, state.institutionGroups),
    [getFilteredIds, id, reportSubmissionGroupId, state.bankTypes, state.institutionGroups])
  const clearFilter = useCallback(() => getFilteredIds(id, reportSubmissionGroupId),
    [getFilteredIds, id, reportSubmissionGroupId])

  const readonly = !outputReport || outputReport.outputReportState === 1

  const getSourceData = useCallback(() => { return { sourceData, cellMetadata } }, [cellMetadata, sourceData])

  const apiMethods = useMemo(() => {
    return {
      saveDataCells: context.api.outputReportService.saveDataCells,
      getRecalculatedCells: context.api.outputReportService.getRecalculatedCells,
      clearReportData: context.api.outputReportService.clearReportData
    }
  }, [])

  const renderFilter = () => <Row className='form'>
    <Col md={5}>
      <FormGroup>
        <Label for='bankTypes'><Resx name='Common.BankTypes' /></Label>
        <DropDownList
          mode='multiple'
          dataSource={context.cache.getBankTypes}
          onChange={selectBankTypes}
          value={state.bankTypes}
        />
      </FormGroup>
    </Col>
    <Col md={5}>
      <FormGroup>
        <Label for='institutionGroups'><Resx name='Common.InstitutionGroups' /></Label>
        <DropDownList
          mode='multiple'
          dataSource={context.api.institutionGroupService.getTableData}
          onChange={selectInstitutionGroups}
          value={state.institutionGroups}
        />
      </FormGroup>
    </Col>
    <Col md={2}>
      <FormGroup>
        <ButtonGroup vertical>
          <Button color='primary' onClick={getFiltered} ><Resx name='Common.ApplyFilter' /></Button>
          <Button color='danger' onClick={clearFilter}><Resx name='Common.ClearFilters' /></Button>
        </ButtonGroup>
      </FormGroup>
    </Col>
  </Row>

  if (isLoading) { return <LoadingIndicator /> }
  if (outputReport) {
    return <EditableTableContext apiMethods={apiMethods}
      getSourceData={getSourceData}
      reportTable={outputReport}
      sourceDataDispatch={actionTypes.SourceDataUpdated}>
      {outputReport.multipleInstitutions && renderFilter()}
      {!readonly && <Row className='text-right'>
        <Col sm='12'><TableTools readonly={readonly} /></Col>
      </Row>}
      <Row>
        <Col sm='12'>
          <ErrorBoundary>
            <ReportTable
              isRtl={isRtl}
              sourceHeaders={sourceHeaders}
              sourceRowHeaders={sourceRowHeaders}
              sourceData={sourceData}
              defaultMetadata={{ readOnly: readonly }}
              cellMetadata={cellMetadata}
              renderAllRows
              readOnly={readonly} />
          </ErrorBoundary>
        </Col>
      </Row>
      {!readonly && <Row className='text-right'>
        <Col sm='12'>
          <Button disabled={readonly} color='danger' onClick={onReportLock}><Resx name='Reporting.LockReport' /></Button>
        </Col>
      </Row>}
    </EditableTableContext>
  }
  return <React.Fragment />
}

OutputReportTable.propTypes = {
  sourceData: propTypes.array,
  isLoading: propTypes.bool,
  outputReport: propTypes.object,
  sourceHeaders: propTypes.object,
  sourceRowHeaders: propTypes.object,
  isRtl: propTypes.bool,
  cellMetadata: propTypes.array,
  rowFilter: propTypes.object
}

export { OutputReportTable }



// WEBPACK FOOTER //
// ./src/internal-app/pages/outputReports/outputReportTable.js